import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TwilioError } from 'twilio-video';
import { ActionAuth, AxiosAuth } from '..';
import { AppConstants, PlatformApi } from '../../configs';
import { useWebSocket } from '../../pages/hooks';
import { logger } from '../../utils';
import { useAppSelector, VRIAppStateType } from '../Redux';


export const SocketContext = React.createContext<{
  socketConnection: WebSocket | null;
  reconnectSocket: () => void;
  loadTwilioToken: () => void;
  token: string | null;
}>({
  socketConnection: null,
  reconnectSocket: () => {},
  loadTwilioToken: () => {},
  token: null,
});

export function SocketContextProvider(
  props: React.PropsWithChildren<{}>
): JSX.Element {
  const dispatch = useDispatch();
  const { webToken, isLoggedIn } = useAppSelector((state: VRIAppStateType) => ({
    profileId: state.auth.profile?.id,
    isLoggedIn: state.auth.isLoggedIn,
    webToken: state.auth.VRItoken,
  }));
  // const webTokenRef = useRef(webToken);
  const { webSocketConnection: socketConnection, reconnect } = useWebSocket();
  const [token, setToken] = useState<string | null>(webToken || null);

  const loadTwilioToken = useCallback(() => {
    logger({ message: 'Getting WebToken' });
    return AxiosAuth.get(PlatformApi.Authentication.Token())
      .then((response) => {
        if (!!response.data && response.status === AppConstants.Api.StatusOK) {
          const token = response.data.token;
          setToken(token);
          dispatch(ActionAuth.SetVRItoken(token));
        }
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          logger({
            message: 'Error: Fetching WebToken failed: ',
            data: error.response,
          });
          throw error;
        }
      });
  }, [dispatch]);

  const socketConnectionMemo = useMemo(
    () => ({
      socketConnection,
      reconnectSocket: reconnect,
      loadTwilioToken,
      token,
    }),
    [socketConnection, reconnect, loadTwilioToken, token]
  );

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }
    loadTwilioToken();
  }, [isLoggedIn, loadTwilioToken]);

  return (
    <SocketContext.Provider value={socketConnectionMemo}>
      {props.children}
    </SocketContext.Provider>
  );
}
